import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FundListDetail } from '@nemo/api';
import { LIST_COLUMNS, LIST_COLUMN_KEYS } from '@nemo/core/model';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { FundsGrid } from '../FundsGrid.js';
import { download } from '../grid/csv.js';
import { ExportButton } from '../sheets/ExportButton.js';
import { SuggestDrawer } from '../suggest/SuggestDrawer.js';
import { useTRPC } from '../trpc.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { Popover } from '../ui/Popover.js';
import { Select } from '../ui/Select.js';
import { useToast } from '../ui/Toast.js';
import { type FundListActions, useFundList } from './useFundList.js';

/**
 * One list: what it is called and who it is for, the words the founder reads
 * first, which columns go out, and the funds themselves in the funds grid,
 * restricted to the list and in its order. Sharing it is a Google Sheet or the CSV.
 */
export function ListPage({ params }: { params: { id: string } }): React.JSX.Element {
  const fundList = useFundList(params.id);
  const list = fundList.list;
  if (fundList.error !== null) return <p className="subtle loading">{fundList.error}</p>;
  if (list === undefined) return <p className="subtle loading">Loading the list…</p>;
  return (
    <div className="page">
      <ListHead list={list} actions={fundList} />
      <FundsGrid scope={{ listId: list.id }} />
    </div>
  );
}

function ListHead(props: { list: FundListDetail; actions: FundListActions }): React.JSX.Element {
  const { list, actions } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const companies = useQuery(trpc.portfolio.list.queryOptions());
  const [name, setName] = useState(list.name);
  const [intro, setIntro] = useState(list.intro ?? '');
  // The Columns button the picker hangs from, while it is open.
  const [picker, setPicker] = useState<HTMLButtonElement | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const deleted = list.deletedAt !== null;

  // Another tab, or the MCP, may have renamed it: follow the stored value
  // whenever it changes underneath.
  useEffect(() => setName(list.name), [list.name]);
  useEffect(() => setIntro(list.intro ?? ''), [list.intro]);

  const remove = useMutation(
    trpc.lists.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
        navigate('/lists');
      },
      onError: (error) => toast(error.message),
    }),
  );

  const restore = useMutation(
    trpc.lists.restore.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.lists.get.queryKey({ id: list.id }) });
      },
      onError: (error) => toast(error.message),
    }),
  );

  async function exportCsv(): Promise<void> {
    setExporting(true);
    try {
      const file = await queryClient.fetchQuery(
        trpc.lists.exportCsv.queryOptions({ id: list.id }, { staleTime: 0 }),
      );
      download(file.filename, file.csv);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'The export did not work.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      {list.deletedAt !== null && (
        <div className="list-deleted-banner" role="status">
          <span>
            This list was deleted on{' '}
            {new Date(list.deletedAt).toLocaleDateString(undefined, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            .
          </span>
          <button
            className="button small"
            type="button"
            disabled={restore.isPending}
            onClick={() => restore.mutate({ id: list.id })}
          >
            Restore
          </button>
        </div>
      )}
      <div className="list-head">
        <div className="list-head-row">
          <input
            className="list-name"
            aria-label="List name"
            disabled={deleted}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() !== '' && name.trim() !== list.name) actions.update({ name });
              else setName(list.name);
            }}
          />
          <Select
            label="For which portfolio company"
            disabled={deleted}
            className="list-company"
            value={list.portfolioCompanyId ?? ''}
            onChange={(value) =>
              actions.update({ portfolioCompanyId: value === '' ? null : value })
            }
            options={[
              { value: '', label: 'For no company in particular' },
              ...(companies.data ?? []).map((company) => ({
                value: company.id,
                label: `For ${company.name}`,
              })),
            ]}
          />
          <span className="spacer" />
          <button
            className="button"
            type="button"
            disabled={deleted}
            onClick={() => setSuggesting(true)}
          >
            Suggest funds
          </button>
          <button
            className="button"
            type="button"
            aria-expanded={picker !== null}
            disabled={deleted}
            onClick={(event) => {
              const trigger = event.currentTarget;
              setPicker((current) => (current === null ? trigger : null));
            }}
          >
            Columns
          </button>
          <ExportButton
            disabled={exporting || list.items.length === 0}
            downloadCsv={() => void exportCsv()}
            scope={() => ({ listId: list.id })}
          />
          {!deleted && (
            <button className="button danger" type="button" onClick={() => setDeleting(true)}>
              Delete list
            </button>
          )}
        </div>
        <textarea
          className="list-intro"
          aria-label="Intro for the founder"
          disabled={deleted}
          placeholder="A few lines for the founder: what this list is and how to use it."
          rows={2}
          value={intro}
          onChange={(event) => setIntro(event.target.value)}
          onBlur={() => {
            if (intro.trim() !== (list.intro ?? ''))
              actions.update({ intro: intro.trim() || null });
          }}
        />
      </div>
      {picker !== null && (
        <Popover
          anchor={{ kind: 'element', element: picker }}
          align="end"
          className="column-picker"
          onClose={() => setPicker(null)}
        >
          <ColumnPicker list={list} actions={actions} />
        </Popover>
      )}
      {suggesting && (
        <SuggestDrawer
          listId={list.id}
          companyId={list.portfolioCompanyId}
          title="Suggest funds"
          onClose={() => setSuggesting(false)}
        />
      )}
      {deleting && (
        <ConfirmModal
          title={`Delete “${list.name}”?`}
          body="The list moves to Deleted on the Lists page, with its notes and intros, and can be restored."
          confirmLabel="Delete list"
          danger
          onConfirm={() => {
            setDeleting(false);
            remove.mutate({ id: list.id });
          }}
          onCancel={() => setDeleting(false)}
        />
      )}
    </>
  );
}

/**
 * Which columns the list shows and exports, in the vocabulary's order, the
 * internal ones said as such; then the columns this list added for itself.
 * Ticking one on puts it at the end; dragging a header in the grid orders it.
 */
function ColumnPicker(props: {
  list: FundListDetail;
  actions: FundListActions;
}): React.JSX.Element {
  const { list, actions } = props;
  const [label, setLabel] = useState('');
  const on = new Set(list.columns);

  function toggle(key: string): void {
    actions.update({
      columns: on.has(key)
        ? list.columns.filter((column) => column !== key)
        : [...list.columns, key],
    });
  }

  return (
    <div className="column-picker-body">
      <fieldset>
        <legend className="subtle">Columns</legend>
        {LIST_COLUMN_KEYS.map((key) => (
          <label key={key} className="check">
            <input type="checkbox" checked={on.has(key)} onChange={() => toggle(key)} />
            {LIST_COLUMNS[key].label}
            {LIST_COLUMNS[key].internal && <span className="tag">internal</span>}
          </label>
        ))}
      </fieldset>
      {list.extraColumns.length > 0 && (
        <fieldset>
          <legend className="subtle">This list's own</legend>
          {list.extraColumns.map((column) => (
            <div key={column.key} className="check">
              <label>
                <input
                  type="checkbox"
                  checked={on.has(column.key)}
                  onChange={() => toggle(column.key)}
                />
                {column.label}
              </label>
              <button
                className="text-button"
                type="button"
                aria-label={`Remove the column ${column.label} and what it holds`}
                onClick={() => actions.removeColumn(column.key)}
              >
                Remove
              </button>
            </div>
          ))}
        </fieldset>
      )}
      <form
        className="column-picker-add"
        onSubmit={(event) => {
          event.preventDefault();
          if (label.trim() === '') return;
          actions.addColumn(label.trim());
          setLabel('');
        }}
      >
        <input
          placeholder="Add a column for this list"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <button className="button small" type="submit" disabled={label.trim() === ''}>
          Add
        </button>
      </form>
    </div>
  );
}
